import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useSyncExternalStore } from "react";

import { SALES_COACH_V2_SCORING_FEATURE } from "constants/feature-flags";
import { useGetSalesPlaybookQuery } from "services/sales-coach";

import { SALES_COACH_V2_FRAMEWORK_VERSION } from "../sales-coach.constants";

export type SalesCoachVersion = "v1" | "v2";

/**
 * How long the probe may take before this page load settles on v1.
 *
 * Longer than the route gate's 3s on purpose: the playbook catalog is a large read, and a
 * slow but healthy load must not quietly drop a flagged user to v1 for the whole page load.
 * This only guards against a request that never answers.
 */
export const VERSION_PROBE_GRACE_MS = 10_000;

/*
 * Which version this page load uses, decided once and shared by every surface.
 *
 * A module latch rather than React context, because two readers sit where no provider can
 * reach them: the layout's own Training badge (above anything the layout renders) and the
 * scribe tab, which has no prop path from a Sales Coach host. Read through
 * `useSyncExternalStore`, so no Redux and no provider — a bare-rendered test reads v1.
 *
 * Latched ONCE: a later refetch or error never returns it to `undefined`, which would
 * unmount the section mid-session. The live rollback path is the LaunchDarkly flag, which
 * `useSalesCoachVersion` reads on every render.
 */
let latched: SalesCoachVersion | undefined;
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getLatched() {
  return latched;
}

function latch(version: SalesCoachVersion) {
  if (latched !== undefined) return;
  latched = version;
  listeners.forEach((listener) => listener());
}

/** Tests only: module state outlives a test within one file. */
export function __resetSalesCoachVersion() {
  latched = undefined;
  listeners.forEach((listener) => listener());
}

function useIsV2FlagOn() {
  // Never a truthy coercion: an absent flag and a flag set to false must both read as off.
  return useFlags()[SALES_COACH_V2_SCORING_FEATURE] === true;
}

/**
 * The version every Sales Coach read and every v2-only render keys on.
 *
 * `"v1"` immediately when the flag is off. Otherwise the latched probe answer, and
 * `undefined` until a probe has run — render nothing that fetches until then.
 *
 * Both callers of the probe render only after a gate has seen a loaded flag set
 * (`useSalesCoachRouteAccess`), so a cold LaunchDarkly load, where `useFlags()` is `{}`,
 * never reaches here and reads as v1.
 */
export default function useSalesCoachVersion(): SalesCoachVersion | undefined {
  const isFlagOn = useIsV2FlagOn();
  const version = useSyncExternalStore(subscribe, getLatched, getLatched);
  return isFlagOn ? version : "v1";
}

/**
 * Settles the version for this page load: v2 only if the backend lists the v2 catalog.
 *
 * Called by exactly two roots — the section layout and `useSalesCoachReviewAccess` — and
 * skipped wherever the caller would not fetch anyway, so an appointment page that shows no
 * Sales Coach card never downloads the catalog.
 */
export function useSalesCoachVersionProbe({ skip }: { skip: boolean }): void {
  const isFlagOn = useIsV2FlagOn();
  const version = useSyncExternalStore(subscribe, getLatched, getLatched);
  const isSkipped = skip || !isFlagOn || version !== undefined;

  const { currentData, isError } = useGetSalesPlaybookQuery(undefined, {
    skip: isSkipped
  });

  useEffect(() => {
    if (isSkipped) return undefined;
    if (currentData) {
      latch(
        currentData.available_versions.includes(
          SALES_COACH_V2_FRAMEWORK_VERSION
        )
          ? "v2"
          : "v1"
      );
      return undefined;
    }
    if (isError) {
      latch("v1");
      return undefined;
    }
    const timer = setTimeout(() => latch("v1"), VERSION_PROBE_GRACE_MS);
    return () => clearTimeout(timer);
  }, [isSkipped, currentData, isError]);
}
